import { useApolloClient } from '@apollo/client';
import { ImgElement } from '@axinom/mosaic-ui';
import clsx from 'clsx';
import * as lscache from 'lscache';
import { charsets, PasswordPolicy } from 'password-sheriff';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ensureError } from '../../../common/errors/ensure-error';
import {
  useAuthenticateTenantAdminMutation,
  useChangeTenantAdminPasswordMutation,
} from '../../../generated/id-service-env-administration';
import arrow from '../../../images/ArrowRight.svg';
import logo from '../../../images/AxinomLogo.png';
import type { BreadcrumbResolver } from '../../../models/breadcrumb';
import { authStateStore } from '../../../state-store';
import { extractErrorString } from '../../../util/helper';
import classes from './ChangePassword.module.scss';

export interface Props {
  isFirstTime?: boolean;
  currentPwd?: string;
}
export const ChangePassword: React.FC<Props> = ({
  isFirstTime,
  currentPwd,
}) => {
  const apolloClient = useApolloClient();
  const history = useHistory();
  const [changePwd] = useChangeTenantAdminPasswordMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });
  const [loginUser] = useAuthenticateTenantAdminMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });
  const [error, setError] = React.useState('');
  const [password, setPassword] = useState(currentPwd || '');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const tenantAdminName = authStateStore.useState('tenantAdminName');
  const tenantId = authStateStore.useState('tenantId');
  const tenantAdminEmail = authStateStore.useState('tenantAdminEmail');

  const adminPasswordPolicy = new PasswordPolicy({
    length: { minLength: 10 },
    containsAtLeast: {
      atLeast: 3,
      expressions: [
        charsets.lowerCase,
        charsets.upperCase,
        charsets.numbers,
        charsets.specialCharacters,
      ],
    },
  });

  const validatePasswords = (): boolean => {
    if (newPassword !== newPassword2) {
      setError('Confirmation password should match with the password');
      return false;
    }

    if (adminPasswordPolicy.check(newPassword) !== true) {
      setError('Password does not meet the above policy requirements.');
      return false;
    }

    return true;
  };

  const handleChangePassword = async (): Promise<void> => {
    if (busy) {
      return;
    }

    setError('');

    if (isFirstTime) {
      setPassword(currentPwd as string);
    }

    if (!validatePasswords()) {
      return;
    }

    try {
      setBusy(true);
      await changePwd({
        variables: {
          dto: {
            password: isFirstTime ? (currentPwd as string) : password,
            newPassword,
          },
        },
      });
      // Authenticate with the new password
      const authRes = await loginUser({
        variables: {
          input: {
            email: tenantAdminEmail,
            password: newPassword,
            tenantId,
          },
        },
      });
      setBusy(false);

      const authResponse = authRes.data?.authenticateTenantAdmin;
      if (authResponse) {
        if (isFirstTime) {
          // let SignIn component to update local storage
          lscache.remove('requirePasswordChange');

          // Trigger the update of the render-tree by updating the auth-state provider context
          authStateStore.setState({ accessToken: authResponse.accessToken });
        } else {
          history.goBack();
        }
      } else {
        setError('Unexpected error occurred while signing in');
      }
    } catch (e) {
      const error = ensureError(e);
      setBusy(false);
      setError(extractErrorString(error));
    }
  };

  return (
    <form className={classes.container}>
      <div className={classes.wrapper}>
        <div className={classes.logo}>
          <ImgElement src={logo} alt="Axinom Logo" />
        </div>
        <div className={classes.box}>
          <div className={classes.login}>New Password</div>
          {isFirstTime && (
            <div className={classes.subText}>
              Welcome {tenantAdminName}. Please set a new password to proceed
            </div>
          )}
          <div className={classes.subText}>Password policy:</div>

          <div className={classes.policy}>
            <pre>{adminPasswordPolicy.toString()}</pre>
          </div>
          <span>{error}</span>

          {!isFirstTime && (
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Current Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
          <input
            type="password"
            autoComplete="new-password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <input
            type="password"
            autoComplete="new-password"
            placeholder="New Password Confirmation"
            value={newPassword2}
            onChange={(e) => setNewPassword2(e.target.value)}
          />
          <div>
            <div
              className={clsx(classes.submit, { [classes.disabled]: busy })}
              onClick={handleChangePassword}
            >
              <div className={classes.text}>Confirm</div>
              <div className={classes.icon}>
                <ImgElement src={arrow} decorative={true} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export const ChangePasswordCrumb: BreadcrumbResolver = () => 'Change Password';
